import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaUser, FaStore, FaBox, FaCommentDots, FaGavel, FaEnvelope } from 'react-icons/fa';
import Loader from '../../Components/utils/Loader';

const API = process.env.REACT_APP_Backend_Host_Name;

function AdminManageDispute() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refundToBuyer, setRefundToBuyer] = useState('');
  const [sellerGot, setSellerGot] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [adminMessage, setAdminMessage] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    const fetchDispute = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const res = await axios.get(`${API}/api/v1/admins/disputes/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          setDispute(res.data.dispute);
          const order = res.data.dispute?.orderId;
          if (order?.summary?.total) {
            const maxRef = order.summary.total - (order.summary.serviceFee || 0);
            setRefundToBuyer((maxRef / 2).toFixed(2));
          }
        }
      } catch (err) {
        console.error('Error fetching dispute:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDispute();
  }, [id]);

  const handleResolve = async (e) => {
    e.preventDefault();
    const refund = parseFloat(refundToBuyer);
    if (isNaN(refund) || refund < 0) return;

    setIsResolving(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API}/api/v1/admins/disputes/resolve`,
        {
          disputeId: id,
          refundToBuyer: refund,
          sellerGot: sellerGot ? parseFloat(sellerGot) : undefined,
          adminNote
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate('/chwi-admin/disputes');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to resolve dispute');
    } finally {
      setIsResolving(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!adminMessage.trim()) return;

    setIsSending(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API}/api/v1/admins/disputes/message`,
        { disputeId: id, text: adminMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAdminMessage('');
      const res = await axios.get(`${API}/api/v1/admins/disputes/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDispute(res.data.dispute);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="adminManageDisputePage adminManageDisputeLoading">
        <Loader type="simple" />
      </div>
    );
  }

  if (!dispute) {
    return (
      <div className="adminManageDisputePage">
        <div className="adminDisputeNotFound">
          <p>Dispute not found.</p>
          <Link to="/chwi-admin/disputes" className="adminDisputeBackLink">← Back to Disputes</Link>
        </div>
      </div>
    );
  }

  const order = dispute.orderId;
  const buyer = order?.buyerId;
  const seller = order?.sellerId;
  const product = order?.productInfo?.productId;
  const maxRefund = order?.summary?.total - (order?.summary?.serviceFee || 0) || 0;
  const isResolved = dispute.status === 'resolved';
  const canResolve = dispute.status === 'admin_involved';
  const displayReason = dispute.reason === 'Other' ? dispute.customReason : dispute.reason;
  const deliveryAddr = order?.deliveryAddress;

  const InfoRow = ({ label, value }) => (
    <div className="adminDisputeInfoRow">
      <span className="adminDisputeInfoLabel">{label}</span>
      <span className="adminDisputeInfoValue">{value || 'N/A'}</span>
    </div>
  );

  return (
    <div className="adminManageDisputePage">
      {/* Header */}
      <div className="adminDisputePageHeader">
        <button className="adminDisputeBackBtn" onClick={() => navigate('/chwi-admin/disputes')}>
          <FaArrowLeft size={16} /> Back to Disputes
        </button>
        <div className="adminDisputeHeaderRight">
          <span className={`adminDisputeStatusBadge adminDisputeStatusBadge--${dispute.status}`}>
            {dispute.status.replace('_', ' ')}
          </span>
          <h1 className="adminDisputePageTitle">Manage Dispute</h1>
        </div>
      </div>

      {/* Overview Card */}
      <div className="adminDisputeCard adminDisputeOverviewCard">
        <div className="adminDisputeCardHeader">
          <h2>Dispute Overview</h2>
          <span className="adminDisputeOrderId">#{order?.customOrderId}</span>
        </div>
        <div className="adminDisputeOverviewGrid">
          <div className="adminDisputeOverviewItem">
            <span className="adminDisputeOverviewLabel">Order Total</span>
            <span className="adminDisputeOverviewValue">${order?.summary?.total?.toFixed(2)}</span>
          </div>
          <div className="adminDisputeOverviewItem">
            <span className="adminDisputeOverviewLabel">Max Refund</span>
            <span className="adminDisputeOverviewValue">${maxRefund.toFixed(2)}</span>
          </div>
          <div className="adminDisputeOverviewItem">
            <span className="adminDisputeOverviewLabel">Started By</span>
            <span className="adminDisputeOverviewValue">{dispute.initiatedBy === 'buyer' ? 'Buyer' : 'Seller'}</span>
          </div>
          <div className="adminDisputeOverviewItem adminDisputeOverviewItem--full">
            <span className="adminDisputeOverviewLabel">Reason</span>
            <span className="adminDisputeOverviewValue adminDisputeReasonText">{displayReason}</span>
          </div>
        </div>
      </div>

      {/* Parties & Order - Two Column */}
      <div className="adminDisputeTwoCol">
        {/* Buyer Card */}
        <div className="adminDisputeCard adminDisputePartyCard">
          <div className="adminDisputeCardHeader adminDisputeCardHeader--buyer">
            <FaUser size={18} />
            <h2>Buyer</h2>
          </div>
          <div className="adminDisputeCardBody">
            <InfoRow label="Name" value={order?.buyerInfo?.fullName || buyer?.username} />
            <InfoRow label="Email" value={order?.buyerInfo?.email || buyer?.email} />
            <InfoRow label="Phone" value={order?.buyerInfo?.phone} />
            {deliveryAddr && (
              <InfoRow
                label="Address"
                value={[deliveryAddr.address, deliveryAddr.city, deliveryAddr.state?.name, deliveryAddr.country?.name].filter(Boolean).join(', ') || 'N/A'}
              />
            )}
            <Link to={`/chwi-admin/chats/?p=${buyer?._id}`} className="adminDisputeContactBtn">
              <FaEnvelope size={14} /> Contact Buyer
            </Link>
          </div>
        </div>

        {/* Seller Card */}
        <div className="adminDisputeCard adminDisputePartyCard">
          <div className="adminDisputeCardHeader adminDisputeCardHeader--seller">
            <FaStore size={18} />
            <h2>Seller</h2>
          </div>
          <div className="adminDisputeCardBody">
            <InfoRow label="Name" value={seller?.displayName || seller?.userId?.username} />
            <InfoRow label="Email" value={seller?.userId?.email} />
            <Link to={`/chwi-admin/chats/?p=${seller?.userId?._id}`} className="adminDisputeContactBtn">
              <FaEnvelope size={14} /> Contact Seller
            </Link>
          </div>
        </div>
      </div>

      {/* Order Details Card */}
      <div className="adminDisputeCard">
        <div className="adminDisputeCardHeader adminDisputeCardHeader--order">
          <FaBox size={18} />
          <h2>Order Details</h2>
        </div>
        <div className="adminDisputeOrderContent">
          <div className="adminDisputeProductRow">
            {product?.thumbnail && (
              <img src={product.thumbnail} alt="" className="adminDisputeProductThumb" />
            )}
            <div className="adminDisputeProductInfo">
              <strong>{order?.productInfo?.title}</strong>
              <span className="adminDisputeProductMeta">
                {order?.productInfo?.listingType === 'service' ? 'Service' : 'Product'} • Qty: {order?.summary?.quantity}
              </span>
            </div>
          </div>
          <div className="adminDisputePricingTable">
            <InfoRow label="Subtotal" value={`$${order?.summary?.subtotal?.toFixed(2)}`} />
            <InfoRow label="Delivery Fee" value={`$${order?.summary?.deliveryFee?.toFixed(2)}`} />
            <InfoRow label="Service Fee" value={`$${order?.summary?.serviceFee?.toFixed(2)}`} />
            <InfoRow label="Tax" value={`$${order?.summary?.tax?.toFixed(2)}`} />
            <div className="adminDisputeInfoRow adminDisputeInfoRow--total">
              <span className="adminDisputeInfoLabel">Total Paid</span>
              <span className="adminDisputeInfoValue">${order?.summary?.total?.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Dispute Activity */}
      {dispute.messages?.length > 0 && (
        <div className="adminDisputeCard">
          <div className="adminDisputeCardHeader adminDisputeCardHeader--activity">
            <FaCommentDots size={18} />
            <h2>Dispute Activity</h2>
          </div>
          <div className="adminDisputeActivityList">
            {dispute.messages.map((msg, i) => (
              <div key={i} className={`adminDisputeActivityItem adminDisputeActivityItem--${msg.senderRole}`}>
                <span className="adminDisputeActivityRole">{msg.senderRole}</span>
                <p className="adminDisputeActivityText">{msg.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Admin Message (when not resolved) */}
      {!isResolved && (
        <div className="adminDisputeCard">
          <div className="adminDisputeCardHeader">
            <h2>Add Message</h2>
          </div>
          <form onSubmit={handleSendMessage} className="adminDisputeMessageForm">
            <textarea
              value={adminMessage}
              onChange={(e) => setAdminMessage(e.target.value)}
              placeholder="Message visible to both parties..."
              className="adminDisputeTextarea"
              rows={3}
            />
            <button type="submit" className="adminDisputeBtn adminDisputeBtn--secondary" disabled={isSending || !adminMessage.trim()}>
              {isSending ? <Loader type="miniPlus" /> : 'Send Message'}
            </button>
          </form>
        </div>
      )}

      {/* Resolve Form */}
      {canResolve && (
        <div className="adminDisputeCard adminDisputeResolveCard">
          <div className="adminDisputeCardHeader adminDisputeCardHeader--resolve">
            <FaGavel size={18} />
            <h2>Resolve Dispute</h2>
          </div>
          <form onSubmit={handleResolve} className="adminDisputeResolveForm">
            <div className="adminDisputeFormGroup">
              <label>Refund to Buyer ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={maxRefund}
                value={refundToBuyer}
                onChange={(e) => setRefundToBuyer(e.target.value)}
                placeholder="0.00"
                required
                className="adminDisputeInput"
              />
              <span className="adminDisputeFormHint">Max: ${maxRefund.toFixed(2)}</span>
            </div>
            <div className="adminDisputeFormGroup">
              <label>Seller Amount ($) <span className="adminDisputeOptional">optional</span></label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={sellerGot}
                onChange={(e) => setSellerGot(e.target.value)}
                placeholder="Auto-calculated if empty"
                className="adminDisputeInput"
              />
            </div>
            <div className="adminDisputeFormGroup">
              <label>Admin Note</label>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder="Internal note for records..."
                rows={2}
                className="adminDisputeTextarea"
              />
            </div>
            <button type="submit" className="adminDisputeBtn adminDisputeBtn--primary" disabled={isResolving}>
              {isResolving ? <Loader type="miniPlus" /> : 'Resolve Dispute'}
            </button>
          </form>
        </div>
      )}

      {/* Resolution Result */}
      {isResolved && dispute.resolution && (
        <div className="adminDisputeCard adminDisputeResolutionCard">
          <div className="adminDisputeCardHeader adminDisputeCardHeader--resolved">
            <h2>Resolution</h2>
          </div>
          <div className="adminDisputeResolutionContent">
            <InfoRow label="Resolved by" value={dispute.resolution.resolvedBy === 'admin' ? 'Admin' : 'Mutual agreement'} />
            <InfoRow label="Refund to buyer" value={`$${dispute.resolution.refundToBuyer?.toFixed(2)}`} />
            <InfoRow label="Seller received" value={`$${dispute.resolution.sellerGot?.toFixed(2)}`} />
            {dispute.resolution.adminNote && (
              <div className="adminDisputeResolutionNote">
                <span className="adminDisputeInfoLabel">Note</span>
                <p>{dispute.resolution.adminNote}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManageDispute;
